import React, { useState, useEffect } from 'react';
import { Send, Wallet, Clock, XCircle, Users, Building2, RefreshCw } from 'lucide-react';
import { useWallet } from '@solana/wallet-adapter-react';
import { useWalletModal } from '@solana/wallet-adapter-react-ui';
import { ApiService, Company, Transaction } from '../services/api';
import { useAuth } from '../providers/AuthProvider';
// Removed unused CopyButton import
import { usePagination } from '../hooks/usePagination';
import toast from 'react-hot-toast';

// Import new components
import SendMoneyModalWrapper from '../components/modals/SendMoneyModalWrapper';
import TransactionDetailsModal from '../components/modals/TransactionDetailsModal';
import TransactionsTable from '../components/tables/TransactionsTable';

// Removed unused CompanyWallet interface

const Settlements: React.FC = () => {
  const [company, setCompany] = useState<Company | null>(null);
  const [transactions, setTransactions] = useState<Transaction[]>([]);
  // Removed unused companyWallets state
  const [loading, setLoading] = useState(true);
  const [refreshing, setRefreshing] = useState(false);
  const [showSendModal, setShowSendModal] = useState(false);
  // Removed unused modal states
  const [showTransactionDetails, setShowTransactionDetails] = useState(false);
  const [selectedTransaction, setSelectedTransaction] = useState<Transaction | null>(null);
  const [availableCompanies, setAvailableCompanies] = useState<{company: Company, exchangeRate: number}[]>([]);
  // Removed unused form states
  const [isSending, setIsSending] = useState(false);
  // const [showProcessingModal, setShowProcessingModal] = useState(false);
  // const [transactionId, setTransactionId] = useState('');
  // const [duration, setDuration] = useState(3);
  // const [slot, setSlot] = useState(0);
  // const [selectedReceiverWallet, setSelectedReceiverWallet] = useState<string>('');
  
  // Sorting state
  const [sortBy, setSortBy] = useState<string>('');
  const [sortOrder, setSortOrder] = useState<'asc' | 'desc'>('desc');
  
  // Date filtering state
  const [startDate, setStartDate] = useState<string>('');
  const [endDate, setEndDate] = useState<string>('');
  
  // Pagination hook
  const transactionsPagination = usePagination(transactions, { itemsPerPage: 10 });
  
  const { connected, publicKey, signTransaction } = useWallet();
  const { setVisible } = useWalletModal();
  const { user } = useAuth();

  // Sort handler
  const handleSort = (field: string, order: 'asc' | 'desc') => {
    setSortBy(field);
    setSortOrder(order);
  };

  // Date filter handlers
  const handleStartDateChange = (date: string | undefined) => {
    setStartDate(date || '');
  };

  const handleEndDateChange = (date: string | undefined) => {
    setEndDate(date || '');
  };

  const handleClearDateFilter = () => {
    setStartDate('');
    setEndDate('');
  };

  useEffect(() => {
    fetchUserData();
  }, [user]);

  useEffect(() => {
    if (company) {
      fetchUserData();
    }
  }, [sortBy, sortOrder, startDate, endDate]);

  useEffect(() => {
    if (company) {
      fetchAvailableCompanies();
    }
  }, [company]);

  const fetchUserData = async () => {
    try {
      const companyData = await ApiService.getUserCompany();
      if (companyData) {
        setCompany(companyData);
        
        try {
          const [, transactionsData] = await Promise.allSettled([
            ApiService.getUserCompanyWallets(),
            ApiService.getTransactionsByCompanyId(
              companyData.id, 
              undefined, 
              sortBy || undefined, 
              sortOrder,
              startDate && startDate.trim() !== '' ? startDate : undefined,
              endDate && endDate.trim() !== '' ? endDate : undefined
            )
          ]);
          
          // Removed company wallets handling
          
          if (transactionsData.status === 'fulfilled') {
            setTransactions(transactionsData.value);
          } else {
            console.error('Failed to fetch transactions:', transactionsData.reason);
            setTransactions([]);
          }
        } catch (error) {
          console.error('Error fetching company data:', error);
          setTransactions([]);
        }
      } else {
        setCompany(null);
        setTransactions([]);
      }
    } catch (error) {
      console.error('Error fetching user data:', error);
      toast.error('Failed to load user data');
    } finally {
      setLoading(false);
    }
  };

  const fetchAvailableCompanies = async () => {
    if (!company) return;
    
    try {
      const companies = await ApiService.getAllowedCompaniesForSender(company.companyWallet);
      setAvailableCompanies(companies);
    } catch (error) {
      console.error('Error fetching available companies:', error);
      setAvailableCompanies([]);
    }
  };

  const handleRefresh = async () => {
    setRefreshing(true);
    try {
      await ApiService.syncCompanyData();
      toast.success('Data refreshed successfully!');
      await fetchUserData();
    } catch (error) {
      console.error('Error refreshing data:', error);
      toast.error('Failed to refresh data');
    }
    setRefreshing(false);
  };

  const handleSendMoney = async (data: { receiverCompanyId: string; amount: string }) => {
    if (!company) return;

    if (!connected || !publicKey || !signTransaction) {
      toast.error('Please connect your wallet first');
      setVisible(true);
      return;
    }

    // const wallet = {
    //   publicKey,
    //   signTransaction,
    // };

    setIsSending(true);
    try {
      const amountInCents = Math.round(parseFloat(data.amount) * 100);
      console.log('amountInCents', amountInCents);
      // const receiverCompanyWallet = availableCompanies.find(c => c.company.id === data.receiverCompanyId)?.company.companyWallet || '';
      
      // Set the receiver wallet for the processing modal
      // setSelectedReceiverWallet(receiverCompanyWallet);
      
      // Use new escrow flow instead of direct send money
      // const result = await ApiService.createHold({
      //   receiverCompanyWallet,
      //   amount: amountInCents,
      // }, wallet);

      // if (result) {
      //   console.log('Hold created successfully:', result);
      //   console.log('Starting processing modal with escrow flow');
        // setTransactionId(result.holdId || '');
        // setDuration(result.duration || 3);
        // setSlot(result.slot || 0);
        // setShowProcessingModal(true);
        // setTimeout(async () => {
        //   await handleRefresh();
        // }, 4000);
      // } else {
      //   toast.error('Failed to create hold');
      // }
    } catch (error: any) {
      console.error('Error creating hold:', error);
      toast.error(error.message || 'Failed to create hold');
    } finally {
      setIsSending(false);
    }
  };

  const handleViewTransactionDetails = (transaction: Transaction) => {
    setSelectedTransaction(transaction);
    setShowTransactionDetails(true);
  };

  const handleViewOnSolscan = (transaction: Transaction) => {
    if (transaction.solanaSignature) {
      window.open(`https://solscan.io/tx/${transaction.solanaSignature}?cluster=devnet`, '_blank');
    }
  };

  const formatCurrency = (amount: number) => {
    return `$${(amount / 100).toFixed(2)}`;
  };

  if (loading) {
    return (
      <div className="min-h-screen bg-[#0E131A] flex items-center justify-center">
        <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-[#2777EF]"></div>
      </div>
    );
  }

  if (!company) {
    return (
      <div className="min-h-screen bg-[#0E131A] flex items-center justify-center">
        <div className="text-center">
          <h2 className="text-2xl font-bold text-white mb-4">No Company Found</h2>
          <p className="text-gray-400 mb-8">You need to be associated with a company to access settlements.</p>
          <button
            onClick={() => {/* Removed modal functionality */}}
            className="px-6 py-3 bg-[#2777EF] hover:bg-[#0053E2] text-white font-medium rounded-lg transition-colors"
          >
            Request Company Access
          </button>
        </div>
      </div>
    );
  }

  if (company.status === 'PENDING') {
    return (
      <div className="min-h-screen bg-[#0E131A] flex items-center justify-center">
        <div className="text-center">
          <h2 className="text-2xl font-bold text-white mb-4">Account Pending Approval</h2>
          <p className="text-gray-400 mb-8">Your company account is pending approval from the administrator.</p>
          <div className="flex items-center justify-center space-x-2 text-yellow-500">
            <Clock className="h-5 w-5" />
            <span>Waiting for approval...</span>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-[#0E131A]">
      <div className="max-w-9xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
        {/* Header */}
        <div className="mb-6 sm:mb-8">
          <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4">
            <div>
              <h1 className="text-2xl sm:text-3xl font-bold text-white">Settlements</h1>
              <p className="text-sm sm:text-base text-gray-400 mt-1 sm:mt-2">Manage your company's financial transactions</p>
            </div>
            <div className="flex flex-col sm:flex-row gap-2 sm:space-x-3 sm:space-y-0 w-full sm:w-auto">
              <button
                onClick={handleRefresh}
                disabled={refreshing}
                className="w-full sm:w-auto px-4 py-2 bg-[#182233] hover:bg-[#567095]/20 text-white font-medium rounded-lg transition-colors disabled:opacity-50 flex items-center justify-center sm:justify-start"
              >
                <RefreshCw className={`h-4 w-4 ${refreshing ? 'animate-spin' : ''}`} />
              </button>
              <button
                onClick={() => setShowSendModal(true)}
                className="w-full sm:w-auto px-6 py-2 bg-[#2777EF] hover:bg-[#0053E2] text-white font-medium rounded-lg transition-colors flex items-center justify-center sm:justify-start"
              >
                <Send className="h-4 w-4 mr-2" />
                Initiate Settlement
              </button>
            </div>
          </div>
        </div>

        {/* Quick Stats */}
        <div className="grid grid-cols-1 md:grid-cols-4 gap-6 mb-8">
          <div className="bg-[#182233] overflow-hidden shadow rounded-lg">
            <div className="p-5">
              <div className="flex items-center">
                <div className="flex-shrink-0">
                  <Building2 className="h-6 w-6 text-gray-400" />
                </div>
                <div className="ml-5 w-0 flex-1">
                  <dl>
                    <dt className="text-sm font-medium text-gray-400 truncate">Company</dt>
                    <dd className="text-lg font-medium text-white">{company.companyName}</dd>
                  </dl>
                </div>
              </div>
            </div>
          </div>

          <div className="bg-[#182233] overflow-hidden shadow rounded-lg">
            <div className="p-5">
              <div className="flex items-center">
                <div className="flex-shrink-0">
                  <Wallet className="h-6 w-6 text-gray-400" />
                </div>
                <div className="ml-5 w-0 flex-1">
                  <dl>
                    <dt className="text-sm font-medium text-gray-400 truncate">Balance</dt>
                    <dd className="text-lg font-medium text-white">{formatCurrency(company.valueAmount)}</dd>
                  </dl>
                </div>
              </div>
            </div>
          </div>

          <div className="bg-[#182233] overflow-hidden shadow rounded-lg">
            <div className="p-5">
              <div className="flex items-center">
                <div className="flex-shrink-0">
                  <Clock className="h-6 w-6 text-gray-400" />
                </div>
                <div className="ml-5 w-0 flex-1">
                  <dl>
                    <dt className="text-sm font-medium text-gray-400 truncate">Daily Limit</dt>
                    <dd className="text-lg font-medium text-white">{formatCurrency(company.dailyLimit)}</dd>
                    <dd className="text-sm text-gray-400 mb-2">Used: {formatCurrency(company.dailyUsed)}</dd>
                    <div className="w-full bg-gray-700 rounded-full h-2">
                      <div
                        className={`h-2 rounded-full ${
                          (company.dailyUsed / company.dailyLimit) > 0.9 ? 'bg-red-500' :
                          (company.dailyUsed / company.dailyLimit) > 0.7 ? 'bg-yellow-500' :
                          'bg-green-500'
                        }`}
                        style={{
                          width: `${Math.min((company.dailyUsed / company.dailyLimit) * 100, 100)}%`
                        }}
                      ></div>
                    </div>
                    <dd className="text-xs text-gray-400 mt-1">
                      {((company.dailyUsed / company.dailyLimit) * 100).toFixed(1)}% used
                    </dd>
                  </dl>
                </div>
              </div>
            </div>
          </div>

          <div className="bg-[#182233] overflow-hidden shadow rounded-lg">
            <div className="p-5">
              <div className="flex items-center">
                <div className="flex-shrink-0">
                  <Users className="h-6 w-6 text-gray-400" />
                </div>
                <div className="ml-5 w-0 flex-1">
                  <dl>
                    <dt className="text-sm font-medium text-gray-400 truncate">Daily Remaining</dt>
                    <dd className={`text-lg font-medium ${
                      (company.dailyUsed / company.dailyLimit) > 0.9 ? 'text-red-400' :
                      (company.dailyUsed / company.dailyLimit) > 0.7 ? 'text-yellow-400' :
                      'text-green-400'
                    }`}>
                      {formatCurrency(company.dailyLimit - company.dailyUsed)}
                    </dd>
                  </dl>
                </div>
              </div>
            </div>
          </div>
        </div>

        {/* Daily Usage Warning */}
        {company.dailyUsed / company.dailyLimit > 0.8 && (
          <div className={`mb-6 p-4 rounded-lg border ${
            company.dailyUsed / company.dailyLimit > 0.9 
              ? 'bg-red-900/20 border-red-500/50 text-red-300' 
              : 'bg-yellow-900/20 border-yellow-500/50 text-yellow-300'
          }`}>
            <div className="flex items-center">
              <div className="flex-shrink-0">
                {company.dailyUsed / company.dailyLimit > 0.9 ? (
                  <XCircle className="h-5 w-5" />
                ) : (
                  <Clock className="h-5 w-5" />
                )}
              </div>
              <div className="ml-3">
                <h3 className={`text-sm font-medium ${
                  company.dailyUsed / company.dailyLimit > 0.9 ? 'text-red-300' : 'text-yellow-300'
                }`}>
                  {company.dailyUsed / company.dailyLimit > 0.9 ? 'Daily Limit Exceeded' : 'Daily Limit Warning'}
                </h3>
                <div className="mt-2 text-sm">
                  <p>
                    {company.dailyUsed / company.dailyLimit > 0.9 
                      ? 'You have exceeded your daily spending limit. Please contact your administrator to increase the limit.'
                      : `You have used ${((company.dailyUsed / company.dailyLimit) * 100).toFixed(1)}% of your daily limit.`
                    }
                  </p>
                </div>
              </div>
            </div>
          </div>
        )}

        {/* Company Info Card */}
        <div className="bg-[#182233] shadow rounded-lg mb-8">
          <div className="px-4 py-5 sm:p-6">
            <h3 className="text-lg leading-6 font-medium text-white mb-4">Company Information</h3>
            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
              <div>
                <h4 className="text-sm font-medium text-gray-400 mb-2">Company Name</h4>
                <p className="text-sm text-white">{company.companyName}</p>
              </div>
              <div>
                <h4 className="text-sm font-medium text-gray-400 mb-2">Email</h4>
                <p className="text-sm text-white">{company.companyEmail}</p>
              </div>
              <div>
                <h4 className="text-sm font-medium text-gray-400 mb-2">Phone</h4>
                <p className="text-sm text-white">{company.companyPhone || 'Not provided'}</p>
              </div>
              <div>
                <h4 className="text-sm font-medium text-gray-400 mb-2">Address</h4>
                <p className="text-sm text-white">{company.companyAddress || 'Not provided'}</p>
              </div>
              <div>
                <h4 className="text-sm font-medium text-gray-400 mb-2">Min Transaction</h4>
                <p className="text-sm text-white">{formatCurrency(company.minTransactionAmount)}</p>
              </div>
              <div>
                <h4 className="text-sm font-medium text-gray-400 mb-2">Max Transaction</h4>
                <p className="text-sm text-white">{formatCurrency(company.maxTransactionAmount)}</p>
              </div>
            </div>
          </div>
        </div>

        {/* Recent Transactions */}
        <div className="bg-[#182233] shadow rounded-lg">
          <div className="px-4 py-5 sm:p-6">
            <h3 className="text-lg leading-6 font-medium text-white mb-4">Recent Transactions</h3>
            <TransactionsTable
              transactions={transactionsPagination.paginatedData}
              onViewDetails={handleViewTransactionDetails}
              onViewOnSolscan={handleViewOnSolscan}
              sortBy={sortBy}
              sortOrder={sortOrder}
              onSort={handleSort}
              startDate={startDate}
              endDate={endDate}
              onStartDateChange={handleStartDateChange}
              onEndDateChange={handleEndDateChange}
              onClearDateFilter={handleClearDateFilter}
              showDateFilter={true}
              currentPage={transactionsPagination.currentPage}
              totalPages={transactionsPagination.totalPages}
              totalItems={transactionsPagination.totalItems}
              itemsPerPage={transactionsPagination.itemsPerPage}
              onPageChange={transactionsPagination.setCurrentPage}
              onItemsPerPageChange={transactionsPagination.setItemsPerPage}
              showPagination={true}
            />
          </div>
        </div>

        {/* Modals */}
        <SendMoneyModalWrapper
          isOpen={showSendModal}
          onClose={() => setShowSendModal(false)}
          onSubmit={handleSendMoney}
          availableCompanies={availableCompanies}
          currentCompany={company}
          isLoading={isSending}
          // showProcessingModal={showProcessingModal}
          // transactionId={transactionId}
          // slot={slot}
          // duration={duration}
        />

        <TransactionDetailsModal
          isOpen={showTransactionDetails}
          onClose={() => setShowTransactionDetails(false)}
          transaction={selectedTransaction}
          onViewOnSolscan={handleViewOnSolscan}
        />
      </div>
    </div>
  );
};

export default Settlements;